import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Link,
  IconButton,
  Divider,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Facebook,
  Instagram,
  WhatsApp,
  Email
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { contactService } from '../../services/contactService';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Buscar contatos da API
  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => contactService.getContacts(),
    staleTime: 0, // Sempre buscar dados frescos
    cacheTime: 0, // Não usar cache
    retry: 3, // Tentar 3 vezes em caso de erro
  });

  const contact = contactsData?.data?.data?.contact;

  // Função para abrir o grupo do WhatsApp
  const handleWhatsAppClick = () => {
    if (contact?.whatsapp) {
      // Verificar se é um link de grupo ou número de telefone
      let whatsappUrl = contact.whatsapp;
      
      // Se não começar com http/https, assumir que é um número de telefone
      if (!whatsappUrl.startsWith('http')) {
        // Remover caracteres não numéricos
        const phoneNumber = whatsappUrl.replace(/\D/g, '');
        whatsappUrl = `https://wa.me/${phoneNumber}`;
      }
      
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const socialLinks = [
    { 
      icon: <Facebook />, 
      href: contact?.facebook || '#', 
      label: 'Facebook',
      show: !!contact?.facebook,
      color: '#1877F2'
    },
    { 
      icon: <Instagram />, 
      href: contact?.instagram || '#', 
      label: 'Instagram',
      show: !!contact?.instagram,
      color: '#E4405F'
    },
    { 
      icon: <WhatsApp />, 
      href: contact?.whatsapp || '#', 
      label: 'WhatsApp',
      show: !!contact?.whatsapp,
      color: '#25D366'
    },
    { 
      icon: <Email />, 
      href: contact?.email ? `mailto:${contact.email}` : '#', 
      label: 'Email',
      show: !!contact?.email,
      color: '#9D00FF'
    },
  ].filter(link => link.show);

  // Debug temporário
  console.log('Footer Debug:', { contactsData, contact, socialLinks: socialLinks.length });

  const quickLinks = [
    { label: 'Início', href: '/' },
    { label: 'Programação', href: '#programming' },
    { label: 'Eventos', href: '/events' },
    { label: 'Equipe', href: '#team' },
    { label: 'Política de Privacidade', href: '/politica-de-privacidade' },
    { label: 'Termos de Serviço', href: '/termos-de-servico' },
    { label: 'Login DJ', href: '/login' },
    { label: 'Cadastro', href: '/register' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: { xs: 'rgba(10, 10, 14, 0.98)', md: 'rgba(0, 0, 0, 0.9)' },
        backdropFilter: { xs: 'none', md: 'blur(10px)' },
        borderTop: '1px solid rgba(139, 92, 246, 0.2)',
        py: 4,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo and Description */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Orbitron',
                fontWeight: 900,
                background: 'linear-gradient(45deg, #8B5CF6, #06B6D4)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              NostalBeat
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: 1.6,
              }}
            >
              Sua rádio online favorita com música nostálgica dos anos 80, 90 e 2000.
              Sinta a batida da nostalgia!
            </Typography>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              sx={{
                color: '#8B5CF6',
                mb: 2,
                fontWeight: 600,
              }}
            >
              Links Rápidos
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {quickLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    '&:hover': {
                      color: '#8B5CF6',
                      textDecoration: 'underline',
                    },
                    transition: 'color 0.3s ease',
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Social Media */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              sx={{
                color: '#8B5CF6',
                mb: 2,
                fontWeight: 600,
              }}
            >
              Redes Sociais
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {socialLinks.length > 0 ? (
                socialLinks.map((social) => (
                  <IconButton
                    key={social.label}
                    component="a"
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={social.label}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      border: `1px solid ${social.color}40`,
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        color: social.color,
                        borderColor: social.color,
                        bgcolor: `${social.color}20`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 20px ${social.color}40`,
                      },
                      transition: 'all 0.3s ease',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.5rem',
                      },
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))
              ) : (
                <Typography variant="body2" sx={{ opacity: 0.5, fontStyle: 'italic' }}>
                  Redes sociais em breve...
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'rgba(139, 92, 246, 0.2)' }} />

        {/* Copyright */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              textAlign: 'center',
            }}
          >
            © {currentYear} NostalBeat. Todos os direitos reservados.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            Feito com ❤️ para os amantes da música nostálgica
          </Typography>
        </Box>
      </Container>

      {/* Botão Flutuante do WhatsApp */}
      {contact?.whatsapp && (
        <Tooltip title="Entrar no grupo do WhatsApp" placement="left">
          <Fab
            color="primary"
            onClick={handleWhatsAppClick}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              bgcolor: '#25D366',
              color: 'white',
              zIndex: 1000,
              '&:hover': {
                bgcolor: '#128C7E',
                transform: 'scale(1.1)',
                boxShadow: '0 8px 25px rgba(37, 211, 102, 0.4)',
              },
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(37, 211, 102, 0.3)',
            }}
          >
            <WhatsApp sx={{ fontSize: '1.5rem' }} />
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

export default Footer;
